import { ArrowRight, Sparkles } from "lucide-react";
import { HOME } from "@/constants/testIds";
import { resolveImageUrl } from "@/components/ImageUploader";

const DEFAULT_HERO = "https://images.unsplash.com/photo-1535254973040-607b474cb50d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwd2VkZGluZyUyMGNha2V8ZW58MHx8fHwxNzg0ODUxMzI2fDA&ixlib=rb-4.1.0&q=85";

const t = (config, key, fallback) => config?.content?.[key] || fallback;

export default function Hero({ config }) {
  const heroUrl = config?.hero_image_url || DEFAULT_HERO;
  const businessName = config?.business_name || "Cakesbyky";
  const tagline = config?.tagline || "Treats for every occasion!";

  return (
    <section id="top" data-testid={HOME.hero} className="relative overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-sky-50 via-white to-amber-50/40" />
      <div className="max-w-6xl mx-auto px-6 pt-16 pb-24 md:pt-24 md:pb-32">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-amber-200 text-xs tracking-widest uppercase text-amber-600 mb-6">
              <Sparkles className="h-3 w-3" />
              {t(config, "hero_badge", "Custom Sweets Catering")}
            </div>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-[color:var(--brand-navy)] mb-6">
              {businessName}<span className="text-amber-500">.</span>
            </h1>
            <p className="text-xl md:text-2xl text-slate-600 font-light mb-8 max-w-lg leading-relaxed">
              {tagline}
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#book" data-testid={HOME.ctaBook} className="btn-pill btn-navy inline-flex items-center gap-2">
                {t(config, "hero_cta_primary", "Book Your Event")} <ArrowRight className="h-4 w-4" />
              </a>
              <a href="#menu" data-testid={HOME.ctaMenu} className="btn-pill bg-white border border-slate-200 text-slate-700 hover:border-amber-400 hover:text-[color:var(--brand-navy)]">
                {t(config, "hero_cta_secondary", "Browse Menu")}
              </a>
            </div>
            <div className="flex items-center gap-6 mt-10">
              {(t(config, "hero_stat1_number", "") || t(config, "hero_stat1_label", "")) && (
                <div>
                  <p className="font-serif-display text-3xl text-[color:var(--brand-navy)]">{t(config, "hero_stat1_number", "")}</p>
                  <p className="text-xs uppercase tracking-widest text-slate-500">{t(config, "hero_stat1_label", "")}</p>
                </div>
              )}
              {(t(config, "hero_stat1_number", "") || t(config, "hero_stat1_label", "")) && (t(config, "hero_stat2_number", "") || t(config, "hero_stat2_label", "")) && (
                <div className="h-10 w-px bg-slate-200" />
              )}
              {(t(config, "hero_stat2_number", "") || t(config, "hero_stat2_label", "")) && (
                <div>
                  <p className="font-serif-display text-3xl text-amber-500">{t(config, "hero_stat2_number", "")}</p>
                  <p className="text-xs uppercase tracking-widest text-slate-500">{t(config, "hero_stat2_label", "")}</p>
                </div>
              )}
            </div>
          </div>
          <div className="relative">
            <div className="absolute -top-6 -right-6 w-40 h-40 rounded-full bg-amber-200/40 blur-3xl" />
            <div className="absolute -bottom-8 -left-4 w-52 h-52 rounded-full bg-sky-300/40 blur-3xl" />
            <div className="relative aspect-[4/5] rounded-[2.5rem] overflow-hidden border-4 border-white shadow-[0_30px_60px_-15px_rgba(15,44,85,0.30)]">
              <img
                src={resolveImageUrl(heroUrl)}
                alt="Signature cake"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-2xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[color:var(--brand-navy)] to-amber-500" />
                <div>
                  <p className="text-sm font-medium text-slate-900">{t(config, "hero_card_title", "Made-to-order")}</p>
                  <p className="text-xs text-slate-500">{t(config, "hero_card_subtitle", "Every treat customized to your event")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
